import { CheckCircle2, Clock3, Download, FileWarning, Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadMyFile, type MyCase, useMyDocuments, useSubmitMyDocument } from '@/features/hr/belgeler/followupApi'
import { fmtDate } from '@/features/shared/format'
import { apiErrorMessage } from '@/i18n'

const OPEN = ['open', 'correction_requested']
const TONE: Record<string, BadgeTone> = { open: 'danger', correction_requested: 'warning', awaiting_review: 'info', completed: 'success', waived: 'neutral', cancelled: 'neutral' }

/** Belgelerim: personel kendisinden istenen belgeleri görür, son tarihleri takip eder ve yükler. */
export function MyDocumentsPage() {
  const { t } = useTranslation()
  const docs = useMyDocuments()
  const submit = useSubmitMyDocument()
  const input = useRef<HTMLInputElement>(null)
  const [target, setTarget] = useState<MyCase | null>(null)
  const today = new Date().toISOString().slice(0, 10)
  const fail = (e: unknown) => toast.error(apiErrorMessage(e))

  const cases = docs.data?.cases ?? []
  const open = cases.filter((c) => OPEN.includes(c.status))
  const other = cases.filter((c) => !OPEN.includes(c.status))

  const pick = (c: MyCase) => {
    setTarget(c)
    input.current?.click()
  }
  const onFile = (f: File) => {
    if (!target) return
    submit.mutate({ caseId: target.id, file: f }, { onSuccess: () => toast.success(t('me.documents.uploaded', { name: target.title })), onError: fail })
  }

  const row = (c: MyCase, canUpload: boolean) => (
    <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1 basis-60">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {c.title}
          <Badge tone={TONE[c.status] ?? 'neutral'}>{t(`me.documents.status.${c.status}`, { defaultValue: c.status })}</Badge>
        </p>
        <p className="text-xs text-muted-foreground">
          {c.dueAt ? <span className={c.dueAt < today && canUpload ? 'font-medium text-destructive' : ''}>{t('me.documents.due', { date: fmtDate(c.dueAt) })}</span> : t('me.documents.noDue')}
          {c.note ? ` · ${c.note}` : ''}
        </p>
      </div>
      {canUpload ? (
        <Button size="sm" onClick={() => pick(c)} disabled={submit.isPending}>
          {submit.isPending && target?.id === c.id ? <Loader2 className="animate-spin" /> : <Upload />}
          {t(c.status === 'correction_requested' ? 'me.documents.reupload' : 'me.documents.upload')}
        </Button>
      ) : c.status === 'awaiting_review' || c.status === 'completed' ? (
        <Button size="sm" variant="ghost" onClick={() => void downloadMyFile(c.id, c.title).catch(fail)}>
          <Download />
          {t('me.documents.download')}
        </Button>
      ) : null}
    </li>
  )

  return (
    <>
      <PageHeader title={t('me.documents.title')} description={t('me.documents.subtitle')} />
      <input
        ref={input}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          e.target.value = ''
        }}
      />
      {docs.isError ? (
        <ErrorState error={docs.error} onRetry={() => void docs.refetch()} />
      ) : docs.isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <div className="space-y-4">
          <Section title={t('me.documents.open', { count: open.length })} icon={FileWarning} bodyClassName="p-0">
            {open.length ? <ul className="divide-y">{open.map((c) => row(c, true))}</ul> : <EmptyState icon={CheckCircle2} title={t('me.documents.allDone')} />}
          </Section>
          {docs.data?.missing.length ? (
            <Section title={t('me.documents.pendingRequests')} icon={Clock3}>
              <p className="mb-2 text-xs text-muted-foreground">{t('me.documents.pendingHint')}</p>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {docs.data.missing.map((m) => (
                  <li key={m.requirementId}>{m.name}</li>
                ))}
              </ul>
            </Section>
          ) : null}
          {other.length ? (
            <Section title={t('me.documents.history')} icon={CheckCircle2} bodyClassName="p-0">
              <ul className="divide-y">{other.map((c) => row(c, false))}</ul>
            </Section>
          ) : null}
        </div>
      )}
    </>
  )
}
